import PropTypes from "prop-types";
import React, { useContext, useState } from "react";

import { useAppSetting } from "@swa-ui/application";
import { window } from "@swa-ui/browser";
import { Button } from "@swa-ui/core";
import { FlightDataContext, useSSOLogin } from "@swa-ui/inflight";

import styles from "./AccountLoginCallToActionCommand.module.scss";

/**
 * Button that logs the user in. It sends them to the SSO login page. If we don't have
 * flight data yet, it gets the flight data first, then sends them.
 */
export const AccountLoginCallToActionCommand = (props) => {
  const { children, newWindow, role, styleType, targetProps } = props;
  const { getSSOLoginUrl } = useSSOLogin();
  const { dismissFlightDataError, flightData, refetchFlightData } = useContext(FlightDataContext);
  const enableFlightData = useAppSetting("enableFlightData");
  const [loading, setLoading] = useState(false);

  return (
    <div className={styles.loginCommand}>
      <Button {...getAccountLoginButtonProps()}>
        <div>
          <span>{children}</span>
        </div>
      </Button>
    </div>
  );

  function getAccountLoginButtonProps() {
    return {
      "aria-label": props["aria-label"],
      className: styles.loginButton,
      fullWidth: true,
      newWindow,
      onClick: handleAccountLoginButtonClick,
      role,
      styleType,
      submitInProgress: loading,
      type: "submit",
    };
  }

  async function handleAccountLoginButtonClick() {
    if (!enableFlightData || flightData) {
      dismissFlightDataError();
      await redirectToSSO();
    } else {
      setLoading(true);

      try {
        const { success } = await refetchFlightData();

        if (success) {
          await redirectToSSO();
        }
      } finally {
        setLoading(false);
      }
    }
  }

  async function redirectToSSO() {
    const url = await getSSOLoginUrl(targetProps);

    if (newWindow) {
      window.open(url, "_blank");
    } else {
      window.location.href = url;
    }
  }
};

AccountLoginCallToActionCommand.propTypes = {
  /** aria-label text to provide accessibility description for button. */
  "aria-label": PropTypes.string,

  /** child node to render within the Button component. */
  children: PropTypes.node,

  /** role of the Button to provide accessibility. */
  role: PropTypes.string,

  /** style type for the button. */
  styleType: PropTypes.string,

  /** additional props to be sent to useSSOLogin. */
  targetProps: PropTypes.object,
};
